import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { setUserId, setUserProperties } from '../config/amplitude';

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Loading...');

  useEffect(() => {
    const handleCallback = async () => {
      // First check if we have an access_token in the URL (from backend redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        console.error('Reddit OAuth error:', error);
        setStatus('Authentication failed. Redirecting...');
        setTimeout(() => navigate('/', { replace: true }), 2000);
        return;
      }

      // If we have an access token directly in the URL
      if (accessToken) {
        // Store the token
        localStorage.setItem('reddit_token', accessToken);
        
        // Get user info and set Amplitude ID
        try {
          const response = await fetch('https://oauth.reddit.com/api/v1/me', {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });
          const userData = await response.json();
          setUserId(userData.id);
          setUserProperties({
            username: userData.name,
            subreddit: userData.subreddit?.display_name,
            created_utc: userData.created_utc,
            is_gold: userData.is_gold,
            is_mod: userData.is_mod
          });
        } catch (error) {
          console.error('Error getting user info:', error);
        }
        
        setStatus('Login successful! Redirecting...');
        setTimeout(() => navigate('/feed', { replace: true }), 1500);
        return;
      }

      // If we have a code, exchange it for a token
      if (code) {
        try {
          setStatus('Authenticating with Reddit...');
          
          // Exchange code for token
          const response = await axios.post(API_ENDPOINTS.AUTH_CALLBACK, { code });
          
          if (response.data.access_token) {
            // Store the token securely
            localStorage.setItem('reddit_token', response.data.access_token);
            
            // Store user info if available
            if (response.data.user) {
              localStorage.setItem('reddit_user', JSON.stringify(response.data.user));
              // Set Amplitude user ID and properties
              setUserId(response.data.user.id);
              setUserProperties({
                username: response.data.user.name,
                subreddit: response.data.user.subreddit?.display_name,
                created_utc: response.data.user.created_utc,
                is_gold: response.data.user.is_gold,
                is_mod: response.data.user.is_mod
              });
            }
            
            setStatus('Login successful! Redirecting...');
            
            // Redirect to feed or dashboard
            setTimeout(() => navigate('/feed', { replace: true }), 1500);
          } else {
            throw new Error('No access token received');
          }
        } catch (error) {
          console.error('Error exchanging code for token:', error);
          setStatus('Authentication failed. Redirecting...');
          setTimeout(() => navigate('/', { replace: true }), 2000);
        }
      } else {
        // No code or token found
        setStatus('No authentication data found. Redirecting...');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gradientStart to-gradientEnd">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mb-4"></div>
      <p className="text-white text-lg">{status}</p>
    </div>
  );
}; 